import React, { useState, useEffect } from 'react';
import { MapContainer, MapWrapper } from './styles';

interface MapComponentProps {
  latitude: number;
  longitude: number;
  onLocationChange: (lat: number, lng: number) => void;
}

const MapComponent: React.FC<MapComponentProps> = ({
  latitude,
  longitude,
  onLocationChange,
}) => {
  const [mapCenter, setMapCenter] = useState({
    lat: latitude || 0,
    lng: longitude || 0,
  });
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<any>(null);

  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout | null = null;
    let mapListeners: google.maps.MapsEventListener[] = [];
    let markerListeners: google.maps.MapsEventListener[] = [];

    const initializeMap = async () => {
      const mapElement = document.getElementById('map');
      if (!mapElement || !isMounted) return;

      try {
        if (typeof google === 'undefined' || !google.maps || !google.maps.Map) {
          timeoutId = setTimeout(initializeMap, 100);
          return;
        }

        let AdvancedMarkerElement: any;
        let PinElement: any;

        if (google.maps.importLibrary) {
          const markerLib = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
          AdvancedMarkerElement = markerLib.AdvancedMarkerElement;
          PinElement = markerLib.PinElement;
        } else {
          console.warn('AdvancedMarkerElement não disponível, usando Marker tradicional');
        }

        const googleMap = new google.maps.Map(mapElement, {
          center: { lat: mapCenter.lat, lng: mapCenter.lng },
          zoom: 20,
          mapId: AdvancedMarkerElement ? 'DEMO_MAP_ID' : undefined,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });

        let googleMarker: any;

        if (AdvancedMarkerElement && PinElement) {
          const pin = new PinElement({
            background: 'var(--primary)',
            borderColor: '#a0465a',
            glyphColor: '#ffffff',
          });

          googleMarker = new AdvancedMarkerElement({
            map: googleMap,
            position: { lat: mapCenter.lat, lng: mapCenter.lng },
            content: pin.element,
            gmpDraggable: true,
            title: 'Ajuste a localização',
          });

          const mapClickListener = googleMap.addListener('click', (event: google.maps.MapMouseEvent) => {
            if (event.latLng && isMounted) {
              const lat = event.latLng.lat();
              const lng = event.latLng.lng();
              
              setMapCenter({ lat, lng });
              googleMarker.position = { lat, lng };
              onLocationChange(lat, lng);
            }
          });
          mapListeners.push(mapClickListener);

          const markerDragListener = googleMarker.addListener('dragend', () => {
            if (googleMarker.position && isMounted) {
              const position = googleMarker.position as google.maps.LatLngLiteral;
              setMapCenter({ lat: position.lat, lng: position.lng });
              onLocationChange(position.lat, position.lng);
            }
          });
          markerListeners.push(markerDragListener);
        } else {
          googleMarker = new google.maps.Marker({
            position: { lat: mapCenter.lat, lng: mapCenter.lng },
            map: googleMap,
            draggable: true,
            title: 'Ajuste a localização',
          });

          const mapClickListener = googleMap.addListener('click', (event: google.maps.MapMouseEvent) => {
            if (event.latLng && isMounted) {
              const lat = event.latLng.lat();
              const lng = event.latLng.lng();
              
              setMapCenter({ lat, lng });
              googleMarker.setPosition({ lat, lng });
              onLocationChange(lat, lng);
            }
          });
          mapListeners.push(mapClickListener);

          const markerDragListener = googleMarker.addListener('dragend', () => {
            const position = googleMarker.getPosition();
            if (position && isMounted) {
              const lat = position.lat();
              const lng = position.lng();
              
              setMapCenter({ lat, lng });
              onLocationChange(lat, lng);
            }
          });
          markerListeners.push(markerDragListener);
        }

        if (isMounted) {
          setMap(googleMap);
          setMarker(googleMarker);
          setGoogleMapsLoaded(true);
        }
      } catch (error) {
        console.error('Erro ao carregar Google Maps:', error);
      }
    };

    const loadGoogleMapsScript = () => {
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      
      if (existingScript) {
        if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
          initializeMap();
        } else {
          timeoutId = setTimeout(() => {
            if (isMounted) initializeMap();
          }, 200);
        }
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&callback=Function.prototype`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        timeoutId = setTimeout(() => {
          if (isMounted) initializeMap();
        }, 200);
      };
      script.onerror = () => {
        console.error('Erro ao carregar script do Google Maps');
      };
      document.head.appendChild(script);
    };

    loadGoogleMapsScript();

    // Cleanup function
    return () => {
      isMounted = false;
      
      // Cancelar timeout pendente
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      // Remover event listeners do mapa
      mapListeners.forEach(listener => {
        if (listener) google.maps.event.removeListener(listener);
      });
      
      // Remover event listeners do marker
      markerListeners.forEach(listener => {
        if (listener) google.maps.event.removeListener(listener);
      });
      
      // Limpar referências
      mapListeners = [];
      markerListeners = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (map && marker) {
      const newPosition = { lat: mapCenter.lat, lng: mapCenter.lng };
      map.setCenter(newPosition);
      
      if (marker.position !== undefined) {
        marker.position = newPosition;
      } else if (marker.setPosition) {
        marker.setPosition(newPosition);
      }
    }
  }, [mapCenter, map, marker]);

  return (
    <MapContainer>
      <h3 style={{ marginBottom: '10px', color: '#333' }}>
        Ajuste a localização no mapa
      </h3>
      <p style={{ marginBottom: '15px', color: '#666', fontSize: '14px' }}>
        Clique no mapa ou arraste o marcador para posicionar no local correto
      </p>
      <MapWrapper>
        <div 
          id="map" 
          style={{ 
            width: '100%', 
            height: '100%',
            borderRadius: '8px'
          }}
        />
        {!googleMapsLoaded && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#666'
          }}>
            Carregando mapa...
          </div>
        )}
      </MapWrapper>
      <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
        <p>Latitude: {mapCenter.lat.toFixed(6)}</p>
        <p>Longitude: {mapCenter.lng.toFixed(6)}</p>
      </div>
    </MapContainer>
  );
};

export default MapComponent;
